import type { Except } from '@festina-life/core'
import { useThemeContext } from '@festina-life/ui/theme'
import { LinearGradient } from '@visx/gradient'
import { Group } from '@visx/group'
import dayjs from 'dayjs'
import { type MouseEvent, useId, useMemo, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import {
  ARROW_HEAD_HEIGHT,
  DATE_FOOTER_HEIGHT,
  DATE_HEADING_HEIGHT,
  DOT_RADIUS,
} from './constants'
import { getBoundingTime, getTimelineUnit } from './helpers'
import { TimeIndicators } from './TimeIndicators'
import { useTimelineScale } from './useTimelineScale'

export type MouseOverVersionsParameters<T> = {
  event: MouseEvent<SVGElement>
  versions: T[]
  versionId: string
  earliestVersionDate: Date
}

export type TooltipData<T> = Except<MouseOverVersionsParameters<T>, 'event'>

export type ChildProps<T> = {
  versions: T[]
  lineViewAt: Date
  lineStartDate: Date
  lineEndDate: Date
  lineLength: number
  nodeHeight: number
}

type Props<T> = {
  /** Height of the vertical time lines. */
  height: number
  /** Scale for drawing the arrows.*/
  scale: number
  /** Width of the parent element. */
  parentWidth: number
  children: (props: ChildProps<T>) => React.ReactNode
  latestVersionDate: Date
  earliestVersionDate: Date
  nodeHeight: number
  versions: T[]
  paddingTop: number
}

export const VersionedArrowsGroup = <T,>({
  height,
  scale,
  parentWidth,
  children,
  earliestVersionDate,
  latestVersionDate,
  nodeHeight,
  versions,
  paddingTop,
}: Props<T>) => {
  const { viewAt, onViewAtChanged } = useConfigurationContext()
  const { getTailwindStyle } = useThemeContext()

  const scaledArrowLength = useMemo(() => {
    const defaultArrowLength = parentWidth
      ? parentWidth - ARROW_HEAD_HEIGHT - DOT_RADIUS / 2
      : 0

    return scale * defaultArrowLength
  }, [parentWidth, scale])

  const latestDate =
    dayjs(latestVersionDate).diff(viewAt) > 0 ? latestVersionDate : viewAt

  const timeUnit = getTimelineUnit(earliestVersionDate, latestDate)
  const boundingTimes = getBoundingTime(
    [earliestVersionDate, latestVersionDate, viewAt],
    timeUnit,
  )

  const { pixelsToDate } = useTimelineScale({
    timeLineWidthPx: scaledArrowLength,
    startDate: boundingTimes.from,
    endDate: boundingTimes.to,
  })

  const currentTimeLineId = useId()
  const [isDraggingLine, setIsDraggingLine] = useState(false)
  const [dragPositionX, setDragPositionX] = useState(0)

  const lineViewAt = useMemo(
    () => (isDraggingLine ? pixelsToDate(dragPositionX) : viewAt),
    [dragPositionX, isDraggingLine, pixelsToDate, viewAt],
  )

  return (
    <svg
      width={scaledArrowLength + ARROW_HEAD_HEIGHT}
      height={height + paddingTop + DATE_HEADING_HEIGHT + DATE_FOOTER_HEIGHT}
      onMouseMove={(e) =>
        setDragPositionX(
          e.clientX - e.currentTarget.getBoundingClientRect().left,
        )
      }
      onMouseDown={(e: MouseEvent) =>
        setIsDraggingLine((e.target as SVGElement).id === currentTimeLineId)
      }
      onMouseUp={() => {
        // When the mouse leaves the SVG, the dragging state is removed.
        // Otherwise it's very wonky, needing another click to really "stop" dragging it.
        // Only change the date if the user didn't cancel the drag by leaving.
        if (isDraggingLine) {
          onViewAtChanged(pixelsToDate(dragPositionX))
          setIsDraggingLine(false)
        }
      }}
      onMouseLeave={() => setIsDraggingLine(false)}
      className={isDraggingLine ? 'cursor-ew-resize' : undefined}
    >
      <LinearGradient
        id="arrowGradient"
        from={getTailwindStyle('--color-timeline-from')}
        to={getTailwindStyle('--color-timeline-to')}
        vertical={false}
      />
      <Group top={paddingTop}>
        <TimeIndicators
          currentTimeLineId={currentTimeLineId}
          from={boundingTimes.from}
          to={boundingTimes.to}
          currentDate={lineViewAt}
          endY={height - paddingTop + DATE_HEADING_HEIGHT}
          lengthInPixels={scaledArrowLength}
          unit={timeUnit}
        />
      </Group>
      <Group top={paddingTop + DATE_HEADING_HEIGHT}>
        {children({
          lineViewAt,
          lineStartDate: boundingTimes.from,
          lineEndDate: boundingTimes.to,
          lineLength: scaledArrowLength,
          nodeHeight,
          versions,
        })}
      </Group>
    </svg>
  )
}
